import { motion } from "framer-motion";
import { cn } from "../../lib/cn";
import { AnimatedNumber } from "./AnimatedNumber";

interface GaugeThreshold {
  value: number;
  color: string;
}

interface AnimatedGaugeProps {
  value: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  label?: string;
  format?: (n: number) => string;
  thresholds?: GaugeThreshold[];
  trackColor?: string;
  defaultColor?: string;
  className?: string;
}

const DEFAULT_THRESHOLDS: GaugeThreshold[] = [
  { value: 0.33, color: "var(--color-soft-red)" },
  { value: 0.66, color: "var(--color-ember-orange)" },
  { value: 1.0, color: "var(--color-signal-green)" },
];

function getColorForValue(ratio: number, thresholds: GaugeThreshold[], defaultColor: string): string {
  for (const t of thresholds) {
    if (ratio <= t.value) return t.color;
  }
  return defaultColor;
}

function AnimatedGauge({
  value,
  max = 100,
  size = 80,
  strokeWidth = 6,
  label,
  format,
  thresholds = DEFAULT_THRESHOLDS,
  trackColor = "var(--border-hairline)",
  defaultColor = "var(--color-signal-green)",
  className,
}: AnimatedGaugeProps) {
  const radius = (size - strokeWidth) / 2;
  const arcLength = Math.PI * radius;
  const progress = Math.max(0, Math.min(1, value / max));
  const strokeDashoffset = arcLength * (1 - progress);
  const fillColor = getColorForValue(progress, thresholds, defaultColor);

  return (
    <div
      className={cn("flex flex-col items-center gap-1", className)}
      role="meter"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label}
    >
      <div className="relative" style={{ width: size, height: size / 2 + strokeWidth }}>
        <svg
          width={size}
          height={size / 2 + strokeWidth}
          viewBox={`0 0 ${size} ${size / 2 + strokeWidth}`}
        >
          <path
            d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
            fill="none"
            stroke={trackColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          <motion.path
            d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
            fill="none"
            stroke={fillColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={arcLength}
            initial={{ strokeDashoffset: arcLength }}
            animate={{ strokeDashoffset }}
            transition={{ type: "spring", stiffness: 60, damping: 15 }}
          />
        </svg>
        <div className="absolute inset-0 flex items-end justify-center pb-0.5">
          <AnimatedNumber
            value={value}
            format={format}
            className="text-sm font-semibold text-[var(--text-primary)]"
          />
        </div>
      </div>
      {label && (
        <span className="text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
          {label}
        </span>
      )}
    </div>
  );
}

AnimatedGauge.displayName = "AnimatedGauge";

export { AnimatedGauge };
export type { AnimatedGaugeProps, GaugeThreshold };
